import { Result, Spin, Button } from 'antd';
// import { Button } from '../../../components/ui';
import PausePreferences from './PausePreferences';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import React, { useCallback, useEffect } from 'react';
import useUnsubscribeToken from '../../../hooks/useUnsubscribeToken';
import { useLocation } from 'react-router';
import qs from 'query-string';
import { useGetUnsubscriptionStatus } from '../../../query/unsubscribe';
import { useGetAllEmailTypes } from '../../../query/mail';
import { useDeepCompareEffect } from 'react-use';
import { useUnsubscribeOrPause } from '../../../mutation/unsubscribe';
import _ from 'lodash';
import { joiResolver } from '@hookform/resolvers/joi';
import * as Joi from 'joi';
import { useUnsubscribeNextContext } from '../context';
import UnsubscriptionReason from './UnsubscriptionReason';

function getEmailTypeDefaultPreferences(
  emailTypes = [],
  unsubscriptionStatus,
  toUnsubEmailTypeId,
) {
  // Both emailTypeIds is null
  if (
    !unsubscriptionStatus.isUnsubscribed &&
    (!toUnsubEmailTypeId || Number.isNaN(toUnsubEmailTypeId))
  ) {
    return emailTypes.map(e => e.id);
  }
  // User is subscribing, so respect emailTypeId coming from url
  if (!unsubscriptionStatus.isUnsubscribed) {
    const toUnsubscribeEmailType = emailTypes.find(
      e => e.id === toUnsubEmailTypeId,
    );
    return emailTypes
      .filter(e => e.priority >= toUnsubscribeEmailType.priority)
      .map(e => e.id);
  }
  // No emailTypeId in url or no emailTypeId in unsubscription, should be all emails
  if (
    !toUnsubEmailTypeId ||
    Number.isNaN(toUnsubEmailTypeId) ||
    !unsubscriptionStatus.emailTypeId
  ) {
    return emailTypes.map(e => e.id);
  }
  // emailTypeId presents in both url and status
  const currentUnsubscribedEmailType = emailTypes.find(
      e => e.id === unsubscriptionStatus.emailTypeId,
    ),
    toUnsubscribeEmailType = emailTypes.find(e => e.id === toUnsubEmailTypeId);
  // Respect higher priority one (smaller is higher)
  const higherPriorityEmailType =
    currentUnsubscribedEmailType.priority > toUnsubscribeEmailType.priority
      ? toUnsubscribeEmailType
      : currentUnsubscribedEmailType;
  // All email types that has lower priority than current email type
  return emailTypes
    .filter(e => e.priority >= higherPriorityEmailType.priority)
    .map(e => e.id);
}

export default function UnsubscribeForm() {
  const unsubscribeForm = useForm({
    defaultValues: {
      pauseDuration: 3,
      emailTypeIds: [],
      isPaused: true,
      reason: null,
      otherReason: '',
    },
    mode: 'onChange',
    resolver: joiResolver(
      Joi.object().keys({
        isPaused: Joi.boolean(),
        pauseDuration: Joi.when('isPaused', {
          is: true,
          then: Joi.number().valid(3, 6, 12),
          otherwise: Joi.number(),
        }),
        emailTypeIds: Joi.array()
          .items(Joi.number())
          .min(1),
        reason: Joi.when('otherReason', {
          is: '',
          then: Joi.string().allow(null),
          otherwise: Joi.string().allow(''),
        }),
        otherReason: Joi.string().allow(''),
      }),
    ),
  });
  const unsubscribeToken = useUnsubscribeToken();
  const location = useLocation();
  const { emailTypeId, paused } = qs.parse(location.search);
  const { data: unsubscriptionStatus, isLoading } = useGetUnsubscriptionStatus(
    unsubscribeToken,
  );
  const [showRestOfForm, setShowRestOfForm] = React.useState(true);
  const {
    data: emailTypeData = [],
    isLoading: emailTypeLoading,
  } = useGetAllEmailTypes();
  const { isLoading: mutateLoading, mutateAsync } = useUnsubscribeOrPause();
  const { setPage } = useUnsubscribeNextContext();
  useDeepCompareEffect(() => {
    if (!emailTypeData.length || !unsubscriptionStatus) return;
    const defaultEmailTypePreferences = getEmailTypeDefaultPreferences(
      emailTypeData,
      unsubscriptionStatus,
      Number(emailTypeId),
    );
    // Default email type ids
    unsubscribeForm.setValue('emailTypeIds', defaultEmailTypePreferences);
    void unsubscribeForm.trigger();
  }, [emailTypeData, unsubscriptionStatus, emailTypeId, paused]);

  const pauseDuration = unsubscribeForm.watch('pauseDuration');
  useEffect(() => {
    if (pauseDuration) {
      setShowRestOfForm(true);
      unsubscribeForm.setValue('isPaused', true);
      void unsubscribeForm.trigger();
    }
  }, [pauseDuration]);

  const onSubmit = useCallback(
    values => {
      let toUnsubEmailTypeId;
      const validEmailTypes = emailTypeData.filter(e => !!e.publicName),
        validEmailTypeIds = validEmailTypes.map(e => e.id);
      if (values.emailTypeIds.every(e => validEmailTypeIds.includes(e))) {
        toUnsubEmailTypeId = null;
      } else {
        // Fetch the highest priority (lower priority is higher)
        toUnsubEmailTypeId = _.minBy(
          emailTypeData.filter(e => values.emailTypeIds.includes(e.id)),
          'priority',
        ).id;
      }
      // console.log(toUnsubEmailTypeId)
      mutateAsync({
        isPaused: values.isPaused,
        pauseDuration: values.pauseDuration,
        emailTypeId: toUnsubEmailTypeId,
        reason: values.reason,
        otherReason: values.otherReason,
      }).then(() => {
        setPage('success');
      });
    },
    [emailTypeData, mutateAsync],
  );
  return (
    <Spin spinning={isLoading || emailTypeLoading}>
      <FormProvider {...unsubscribeForm}>
        <div
          className="flex items-center w-full"
          style={{ flexDirection: 'column' }}
        >
          <div className="unsub-form" style={{ marginTop: 20 }}>
            <Result
              status="warning"
              style={{ padding: 0 }}
              title={
                <p style={{ fontSize: 20 }}>
                  We're sorry to see you go!
                  <br />
                  Please let us know your preference regarding email
                  communication
                </p>
              }
            />
            <Controller
              control={unsubscribeForm.control}
              name="isPaused"
              render={({ field }) => {
                return (
                  <div className="space-y-2" style={{ marginTop: 20 }}>
                    <div className="flex space-x-2">
                      <input
                        checked={field.value}
                        id="pause"
                        name="fav_language"
                        type="radio"
                        value
                        onChange={() => {
                          field.onChange(true);
                          setShowRestOfForm(true);
                        }}
                      />
                      <label className="cursor-pointer" htmlFor="pause">
                        <b style={{ fontSize: 14 }}>
                          I would like to temporarily pause receiving emails
                        </b>
                      </label>
                    </div>
                    <PausePreferences />
                    <div className="flex space-x-2">
                      <input
                        checked={!field.value}
                        id="unsubscribe"
                        name="fav_language"
                        type="radio"
                        value={false}
                        onChange={() => {
                          field.onChange(false);
                          setShowRestOfForm(true);
                        }}
                      />
                      <label className="cursor-pointer" htmlFor="unsubscribe">
                        <b style={{ fontSize: 14 }}>
                          I would like to permanently unsubscribe from all
                          future email communication.
                        </b>
                      </label>
                    </div>
                  </div>
                );
              }}
            />
            {showRestOfForm && (
              <>
                <UnsubscriptionReason />
                <Button
                  disabled={!unsubscribeForm.formState.isValid}
                  loading={mutateLoading}
                  style={{ marginTop: 40, borderRadius: 3 }}
                  type="primary"
                  onClick={unsubscribeForm.handleSubmit(onSubmit)}
                >
                  Confirm
                </Button>
                <p
                  style={{
                    lineHeight: '24px',
                    marginBottom: 0,
                    marginTop: 20,
                    fontSize: 14,
                    textAlign: 'center',
                  }}
                >
                  <i>
                    Note: By selecting either option, you will no longer receive
                    promotional emails, newsletters, and updates from us.
                    However, please note that important transactional or
                    account-related emails may still be sent to you.
                  </i>
                </p>
              </>
            )}
          </div>
        </div>
      </FormProvider>
    </Spin>
  );
}
